/**
 * Shared matching logic for cmdk <Command> dropdowns.
 *
 * cmdk's built-in filter does loose fuzzy/subsequence scoring against each
 * item's value prop, which can produce false positives and often matches
 * against IDs instead of visible labels.
 *
 * This module provides strict word-start matching against the visible label and
 * a commandFilter ready to drop into <Command filter={commandFilter}>.
 * Pair it with keywords={[label]} on each <CommandItem> when the item's value
 * is an ID; when value is already the label, no keywords are needed.
 */

/** Split text into lowercase word tokens (drops punctuation and spaces). */
function wordsOf(text: string): string[] {
  return text.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
}

/**
 * Rank how well an option's visible text matches the typed search.
 * Higher is better; 0 means "no match" (item hidden).
 *
 * Uses word-start (prefix) matching, like a typical autocomplete: a typed word
 * must match the start of a word in the label, so "an" matches "Aniket" but
 * not the "an" buried inside "Bank". Order of preference: identical, whole
 * label prefix, first-word prefix, then every typed word matching the start
 * of some label word (handles out-of-order queries like
 * "singh aniket"). No mid-word or fuzzy/subsequence matching.
 */
export function scoreLabelMatch(text: string, search: string): number {
  const haystack = text.toLowerCase().trim();
  const needle = search.toLowerCase().trim();

  if (!needle) return 1;
  if (haystack === needle) return 1; // identical
  if (haystack.startsWith(needle)) return 0.95; // whole label prefix

  const labelWords = wordsOf(haystack);
  const queryTokens = wordsOf(needle);
  const allTokensHitWordStart = queryTokens.every((token) =>
    labelWords.some((word) => word.startsWith(token)),
  );
  if (!allTokensHitWordStart) return 0;

  // Rank a leading-word match above an interior-word match.
  return labelWords[0]?.startsWith(queryTokens[0]) ? 0.9 : 0.8;
}

/**
 * Drop-in `filter` for cmdk's `<Command>`. Prefers the item's `keywords` (the
 * human-readable label) and falls back to its `value` when no keywords are set.
 */
export const commandFilter = (value: string, search: string, keywords?: string[]): number =>
  scoreLabelMatch(keywords?.[0] ?? value, search);
